/**
 * Auto-seed gate.
 *
 * On a genuinely-empty wallet the app CAN bootstrap demo data (see
 * ngBootstrap.bootstrapWallet, driven by the auto-seed effect in
 * FestipodDataContext). This used to fire unconditionally in dev, which bloats a
 * persistent wallet over repeated runs. It is now OFF BY DEFAULT and only runs
 * when explicitly enabled via the env var `FESTIPOD_AUTO_SEED` (= "1").
 *
 * Same runtime/compile-time plumbing as the shared-wallet password
 * (sharedWallet.ts): `build.ts` `define`s the global from the env var for a
 * static bundle, and the dev server / `bun run start` expose it at runtime via
 * `/festipod-config.json` (read by frontend.tsx, which sets the global before the
 * app tree loads). Absent env → global undefined → seed OFF.
 *
 * NOTE: this gate only governs the app's AUTOMATIC seed. It is subordinate to
 * `fixtureSeedEnabled()` below, which currently switches EVERY route off.
 */

/**
 * MASTER SWITCH — may a fixture seed be written into the connected wallet AT ALL?
 *
 * Currently OFF, a product decision: no fixture reaches the connected wallet by
 * any route — neither the opt-in auto-seed (`FESTIPOD_AUTO_SEED`, whose gate
 * below is subordinate to this one) nor an explicit `loadTestData()`.
 *
 * OFF, not deleted. The fixtures (`data/seedData.ts`) and the seeding code stay,
 * because two paths still need them and NEITHER writes to a wallet: the
 * demo/disconnected provider (`LocalDataProvider`) reads the fixtures straight
 * into React state, and the rendering-layer tests read them directly too. Only
 * the connected wallet is closed off.
 *
 * Enforced at the single place fixtures enter a wallet — `bootstrapWallet`
 * (`utils/ngBootstrap.ts`) — so no caller can bypass it by construction; the
 * call sites consult it too, only so they neither log nor await work that will
 * not happen.
 */
export const fixtureSeedEnabled = (): boolean => false;

// Build-injected global (not `process.env`, absent in the browser); any path that
// doesn't inject it reads `undefined` → false safely (no ReferenceError).
declare global {
  // eslint-disable-next-line no-var
  var __FESTIPOD_AUTO_SEED__: string | boolean | undefined;
}

/** Truthy value of the auto-seed flag ("1"/"true"/true → enabled). */
function readFlag(): boolean {
  const v = globalThis.__FESTIPOD_AUTO_SEED__;
  if (v === true) return true;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    return s === '1' || s === 'true';
  }
  return false;
}

/**
 * Whether the app's AUTOMATIC empty-wallet seed is enabled. Read at call time so
 * the runtime config set by frontend.tsx (dev/start) is picked up even though it
 * lands after this module first evaluates.
 */
export const autoSeedEnabled = (): boolean => readFlag();

/**
 * The seed-gate decision: should the app AUTO-SEED demo data right now? True ONLY
 * when the gate is enabled (`FESTIPOD_AUTO_SEED`) AND the wallet is genuinely empty
 * (no data to preserve). `walletHasData` is the caller's "synced and non-empty"
 * signal. Extracted as a pure predicate so the gate is unit-testable in isolation
 * from the React effect that consumes it.
 */
export function shouldAutoSeed(walletHasData: boolean): boolean {
  if (!autoSeedEnabled()) return false;
  return !walletHasData;
}
